import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as gmaps from './functions';

const DEBOUNCE_MS = 300;

type Prediction = google.maps.places.AutocompletePrediction;
type SessionToken = google.maps.places.AutocompleteSessionToken;

export function useAddressPredictions(query: string): {
  predictions: Prediction[];
  isLoading: boolean;
  sessionToken: SessionToken | null;
  resetSession: () => void;
} {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState<SessionToken | null>(null);

  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const mapsRef = useRef<gmaps.GoogleMaps | null>(null);
  const sessionTokenRef = useRef<SessionToken | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  // Sync the ref every render so the debounced closure always reads the latest
  // token without needing to be recreated or re-scheduled on token changes.
  sessionTokenRef.current = sessionToken;

  useEffect(() => {
    mountedRef.current = true;
    gmaps
      .googleMaps()
      .then((maps) => {
        if (!mountedRef.current) return;
        mapsRef.current = maps;
        serviceRef.current = new maps.places.AutocompleteService();
        const token = new maps.places.AutocompleteSessionToken();
        sessionTokenRef.current = token;
        setSessionToken(token);
      })
      .catch((e) => console.error('[useAddressPredictions] Failed to load Google Maps', e));
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const runFetch = useMemo(
    () =>
      debounce((input: string) => {
        const service = serviceRef.current;
        const token = sessionTokenRef.current;
        if (!service || !token) return;
        const requestId = ++requestIdRef.current;
        service.getPlacePredictions(
          {
            input,
            sessionToken: token,
            componentRestrictions: { country: 'US' },
            types: ['address'],
          },
          (results, status) => {
            if (!mountedRef.current) return;
            if (requestId !== requestIdRef.current) return;
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              setPredictions(results ?? []);
            } else {
              setPredictions([]);
            }
            setIsLoading(false);
          }
        );
      }, DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      runFetch.cancel();
      setPredictions([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    runFetch(trimmed);
  }, [query, runFetch]);

  useEffect(() => {
    return () => {
      runFetch.cancel();
    };
  }, [runFetch]);

  const resetSession = useCallback(() => {
    const maps = mapsRef.current;
    if (maps) {
      const token = new maps.places.AutocompleteSessionToken();
      sessionTokenRef.current = token;
      setSessionToken(token);
    }
  }, []);

  return { predictions, isLoading, sessionToken, resetSession };
}
